import React, { useState } from 'react';
import { InventoryItem } from '../../api/inventory';
import { foilTreatmentLabel } from '../../lib/cardVariantOptions';
import { isMagicProductLine } from '../../lib/inventoryNav';
import { gameDisplayName } from '../../lib/tcgplayerListing';
import { CardImage } from '../card/CardImage';
import { colors, spacing, typographyFlat, borderRadius } from '../../styles/tokens';

interface ThumbnailCardProps {
  item: InventoryItem;
  onEdit: (id: string) => void;
  isButtonHovered?: boolean;
}

const styles = {
  thumbnailCard: {
    cursor: 'pointer',
    borderRadius: borderRadius.md,
    overflow: 'hidden',
    boxShadow: `0 1px 3px ${colors.text}11`,
    transition: 'all 0.2s ease',
  } as React.CSSProperties,
  thumbnailImage: {
    width: '100%',
    height: '400px',
    objectFit: 'cover' as const,
    display: 'block',
  },
  thumbnailPlaceholder: {
    width: '100%',
    height: '400px',
    backgroundColor: colors.background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.md,
  },
  thumbnailPlaceholderText: {
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.textSecondary,
    textAlign: 'center' as const,
  },
  thumbnailInfo: {
    padding: spacing.md,
    backgroundColor: colors.surface,
  },
  thumbnailTitle: {
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.xs,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
  },
  thumbnailSet: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
    margin: `0 0 ${spacing.xs} 0`,
  },
  thumbnailQty: {
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.primary,
    margin: 0,
  },
  foilBadge: {
    position: 'absolute' as const,
    bottom: spacing.sm,
    right: spacing.sm,
    backgroundColor: 'rgba(255, 215, 0, 0.9)',
    color: '#000',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.bold,
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
    zIndex: 10,
  },
  stapleBadge: {
    position: 'absolute' as const,
    top: spacing.sm,
    left: spacing.sm,
    backgroundColor: 'rgba(138, 43, 226, 0.9)', // Purple
    color: '#fff',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.bold,
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
    zIndex: 10,
  },
  imageContainer: {
    height: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
  },
};

export const ThumbnailCard: React.FC<ThumbnailCardProps> = ({ item, onEdit, isButtonHovered = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  // Disable hover effect when scroll button is hovered
  const shouldShowHover = isHovered && !isButtonHovered;
  const formatPrice = (value?: number | null) => (value != null ? `$${value.toFixed(2)}` : '—');
  const magicChrome = isMagicProductLine(item.product_line);
  const treatment = foilTreatmentLabel(item);
  const showImage = Boolean(item.image_url || (magicChrome && item.scryfall_id));

  return (
    <div
      style={{
        ...styles.thumbnailCard,
        boxShadow: shouldShowHover ? `0 4px 12px ${colors.text}22` : `0 1px 3px ${colors.text}11`,
        transform: shouldShowHover ? 'translateY(-2px)' : 'translateY(0)',
        pointerEvents: isButtonHovered ? 'none' : 'auto',
      } as React.CSSProperties}
      onClick={() => onEdit(item.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showImage ? (
        <div style={styles.imageContainer}>
          <CardImage 
            scryfallId={magicChrome ? item.scryfall_id : undefined}
            reverseScryfallId={magicChrome ? item.reverse_scryfall_id : undefined}
            cardName={item.product_name}
            imageUrl={item.image_url}
            reverseImageUrl={item.reverse_image_url}
            isDualFaced={!!item.is_dual_faced}
            size="md"
          />
          {treatment ? <div style={styles.foilBadge}>{treatment}</div> : null}
          {magicChrome && item.edhrec_rank && item.edhrec_rank <= 500 && <div style={styles.stapleBadge}>STAPLE</div>}
        </div>
      ) : (
        <div style={styles.thumbnailPlaceholder}>
          <span style={styles.thumbnailPlaceholderText}>No image available</span>
        </div>
      )}
      <div style={styles.thumbnailInfo}>
        <h3 style={styles.thumbnailTitle}>{item.product_name}</h3>
        <p style={styles.thumbnailSet}>{gameDisplayName(item.product_line)}</p>
        <p style={styles.thumbnailSet}>{item.set_name}</p>
        <p style={styles.thumbnailQty}>Qty: {item.quantity}</p>
        <p style={styles.thumbnailSet}>Avg Cost: {formatPrice(item.cost_basis)}</p>
        <p style={styles.thumbnailSet}>Preferred: {formatPrice(item.preferred_price)}</p>
        <p style={styles.thumbnailSet}>Policy: {formatPrice(item.policy_price)}</p>
        <p style={styles.thumbnailSet}>Listed: {formatPrice(item.manapool_listing_price)}</p>
        <p style={styles.thumbnailSet}>Low: {formatPrice(item.manapool_low)}</p>
      </div>
    </div>
  );
};
